/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios';
import { inspect } from 'util';

import { processAcceptLanguage, logger } from '@marriott/mi-headless-utils';
import { SERVICE_UNAVIALABLE_PAGE, SYSTEM_MAINTENANCE_URL, PREPROCESSOR_PREFIXES_ALL_PAGES } from './constants';
import { addLocalePrefix } from './helper';

const { NEXT_PUBLIC_PRE_PROCESSING_URL } = process.env;
/**
 *
 * @param sessionID required session id user session token
 * @param req  required param
 * @param extraPara, option params for specifix pages
 * @param customHeaders if have to pass specific headers for particular request
 * @returns
 * response related to page data/props and validate user token
 * and redirect to specific page
 */

export const EXPERIENCE_SEGMENT_CONSTANTS = {
  NotLoggedIN: ['NOT Logged In'],
  LoggedIN: ['Logged In'],
  DarkMode: 'DARKMODE',
  darkModeParam: 'darkModeExp',
  commpVariantParam: 'componentVariation',
};
export const createPreprocessorUrl = (hostUrl: string, version: string, suffix?: string) => {
  return `${hostUrl}/${version}/${PREPROCESSOR_PREFIXES_ALL_PAGES.PREPROCESS}${suffix}`;
};

export const preProcessorCall = async (
  sessionID: string,
  req: any,
  extraParam?: string,
  customHeaders = {},
  urlParams = {},
  endPointVersion = PREPROCESSOR_PREFIXES_ALL_PAGES.VERSION,
  propertyDttDetails: { [key: string]: string } = {}
) => {
  global.loggerInstance = logger({
    sessionID: sessionID,
  }) as CallableFunction;
  const { log } = global.loggerInstance('preProcessorCall');
  log.debug(`[preProcessorCall] extraParam: ${inspect(extraParam)}`);
  log.debug(`[preProcessorCall] req: ${inspect(req)}`);
  const isNuaPage = req?.url?.includes('/loyalty/requestNightlyUpgradeAwards');
  const dtt =
    req?.headers['dtt'] === 'true'
      ? Object.keys(propertyDttDetails)?.length > 0
        ? propertyDttDetails['dtt']
        : req?.headers['dtt']
      : '';
  const headers: any = {
    ...(dtt === 'true'
      ? {
          dtt: dtt,
        }
      : {}),
    Cookie: req?.headers['cookie'] || '',
    'Content-Type': 'application/json',
    'x-request-id': req?.headers['x-request-id'] || '',
    'X-Host': req?.headers['x-host'] ?? '',
    'Accept-Language': req?.headers['Accept-Language'] || req?.headers['accept-language'] || '',
    Referer: req?.headers['Referer'] ?? '',
    maxRedirect: 0,
    ...customHeaders,
  };
  const url = createPreprocessorUrl(NEXT_PUBLIC_PRE_PROCESSING_URL ?? '', endPointVersion, extraParam ?? '');

  log.debug(`[preProcessorCall] API url query-params: ${inspect(urlParams)}`);
  log.debug(`[preProcessorCall] API url: ${url}`);
  log.debug(`[preProcessorCall] headers: ${inspect(headers)}`);
  try {
    const response = await axios.request({
      url,
      method: 'get',
      maxRedirects: 0,
      headers,
      params: { ...urlParams },
      validateStatus: null,
    });
    if (response && response?.status >= 300 && response?.status < 400 && response?.headers?.['location']) {
      log.debug(`[preProcessorCall] preProcessorCall 302 location: ${response?.headers?.['location']}`);
      return {
        data: {
          redirect: {
            destination: `${response?.data?.subDirectoryPrefix || ''}${response?.headers?.['location']}`,
            permanent: false,
          },
          error: true,
        },
      };
    } else {
      return response;
    }
  } catch (e: any) {
    log.debug(`[preProcessorCall] Error ! ${inspect(e?.response?.data)}`);
    return {
      data: {
        redirect: {
          destination: addLocalePrefix(req?.url, isNuaPage ? SERVICE_UNAVIALABLE_PAGE : SYSTEM_MAINTENANCE_URL),
          permanent: false,
        },
        error: true,
      },
    };
  }
};

// Function to get experience segment with dark mode consideration
// @param reques - request object from routes,
// @param etraParam - extra parameters containing existing experience segment
// @returns Array<string> - updated experience segment array

const uniqueArray = (arr: Array<string>) => Array.from(new Set(arr?.filter(key => !!key))); // function to return unique array values without falsy values

const getExperienceSegmentAndResponse = (
  extraParam: any,
  expSegment: Array<string>,
  componentVariation?: string
): Array<string> => {
  /**
   * get existing experience segment from preprocessor extra param
   * and return unique values in experience segment
   */
  return {
    ...extraParam,
    ...(componentVariation && { componentVariation: componentVariation }),
    ...(expSegment && expSegment?.length > 0 ? { EXPERIENCE_SEGMENT: uniqueArray(expSegment) } : {}),
  };
};

const normalizeLocale = (locale?: string | null): string => {
  return locale?.toLowerCase().replaceAll(/_/g, '-') ?? '';
};

const getLocaleEnabledInfo = (config: string | null | undefined, locale: string): boolean => {
  if (!config?.length || !locale) {
    return false;
  }

  const normalizedLocale = normalizeLocale(locale);
  return normalizeLocale(config)?.includes(normalizedLocale);
};

export const getDarkModeExperienceSegment = (context: any, extraParam: any): any => {
  /**
   * Function to get experience segment with dark mode consideration
   * @param context - request object from routes,
   * @param extraParam - extra parameters containing existing experience segment
   * @returns Array<string> - updated experience segment array
   */
  const { NotLoggedIN, LoggedIN, DarkMode, darkModeParam, commpVariantParam } = EXPERIENCE_SEGMENT_CONSTANTS;

  const { featureFlags, EXPERIENCE_SEGMENT } = extraParam;
  const headerData = context?.req?.headers; // get header data
  const query = context?.query; // get query params
  const currentLocale = processAcceptLanguage(headerData?.['accept-language']);

  const isLocaleEnabled = getLocaleEnabledInfo(
    featureFlags?.['experience_mcom_account_enable_darkmode_page'],
    currentLocale
  );

  const queryString = new URLSearchParams(query); // get query params

  const componentVariation = queryString.get(commpVariantParam) || headerData?.[commpVariantParam.toLowerCase()]; // get component variation from query param or header

  const queryDarkModeParams = queryString?.get(darkModeParam); // get dark mode experience segment from query param
  const headersDarkModeParams = headerData?.[darkModeParam.toLowerCase()]; // get dark mode experience segment from headers

  if (queryString.has(darkModeParam) || headersDarkModeParams || isLocaleEnabled) {
    const isAuthenticated = context?.req?.cookies?.UserIdToken || context?.cookies?.UserIdToken ? true : false; // check user is logged in or not
    const expSegment = [...(isAuthenticated ? LoggedIN : NotLoggedIN), ...(EXPERIENCE_SEGMENT ?? [])]; //Always return unique values in experience segment
    if (queryDarkModeParams && queryDarkModeParams !== '' && queryDarkModeParams !== 'true') {
      /**
       * if we need to add extra experience segment for dark mode other than just DARKMODE
       */

      return getExperienceSegmentAndResponse(
        extraParam,
        [...expSegment, ...(queryDarkModeParams?.split('|') || [])],
        componentVariation
      ); //Always return unique values in experience segment with same response object that we got from preprocessor
    } else if (headersDarkModeParams && headersDarkModeParams !== '' && headersDarkModeParams !== 'true') {
      return getExperienceSegmentAndResponse(
        extraParam,
        [...expSegment, ...(headersDarkModeParams?.split('|') || [])],
        componentVariation
      ); //Always return unique values in experience segment with same response object that we got from preprocessor
    }
    return getExperienceSegmentAndResponse(extraParam, [...expSegment, DarkMode], componentVariation);
  } else if (componentVariation) {
    // if only component variation is present in query param or headers
    return {
      ...extraParam,

      componentVariation: componentVariation,
    };
  }
  return extraParam; // return original extraParam if no dark mode present
};
